import { useEffect, useState } from 'react'

export interface UseGenAiHighRiskFlowArgs {
  isSearchingForCoaching: boolean
  isCustomPreferencesModalVisible: boolean
  onNavigateToSearchCareRedirect?: () => void
  onReopenCustomPreferencesModal: () => void
  onPrepareResultsView: () => void
  onCloseCustomPreferencesModal: () => void
}

export interface UseGenAiHighRiskFlowReturn {
  isGenAiHighRiskModalVisible: boolean
  isReopeningAfterGenAiHighRisk: boolean
  hasAcknowledgedHighRisk: boolean
  onGenAiHighRiskDetected: () => void
  onDismissGenAiHighRiskModal: () => void
}

/**
 * Hook to manage GenAI high-risk flow for parse preference responses.
 * Handles showing high-risk modals when GenAI detects concerning content.
 */
export const useGenAiHighRiskFlow = ({
  isSearchingForCoaching,
  isCustomPreferencesModalVisible,
  onNavigateToSearchCareRedirect,
  onReopenCustomPreferencesModal,
  onPrepareResultsView,
  onCloseCustomPreferencesModal,
}: UseGenAiHighRiskFlowArgs): UseGenAiHighRiskFlowReturn => {
  const [isShowingHighRiskModal, setIsShowingHighRiskModal] = useState(false)
  const [isReopeningAfterGenAiHighRisk, setIsReopeningAfterGenAiHighRisk] = useState(false)
  const [hasAcknowledgedHighRisk, setHasAcknowledgedHighRisk] = useState(false)

  const onGenAiHighRiskDetected = () => {
    setIsShowingHighRiskModal(true)
    setHasAcknowledgedHighRisk(false)
    onPrepareResultsView()
  }

  const onDismissGenAiHighRiskModal = () => {
    setIsShowingHighRiskModal(false)
    setHasAcknowledgedHighRisk(true)

    if (isSearchingForCoaching) {
      // For coaching searches, close the custom preferences modal and redirect to SearchCareRedirect
      onCloseCustomPreferencesModal()
      onNavigateToSearchCareRedirect?.()
    } else {
      onPrepareResultsView()
      setIsReopeningAfterGenAiHighRisk(true)
      onReopenCustomPreferencesModal()
    }
  }

  // Manage state transitions based on modal visibility
  useEffect(() => {
    if (isCustomPreferencesModalVisible) {
      // Reset reopening flag when modal becomes visible
      if (isReopeningAfterGenAiHighRisk) {
        setIsReopeningAfterGenAiHighRisk(false)
      }
    } else {
      // Reset high-risk state when modal closes (unless we're in the reopen flow)
      if (!isReopeningAfterGenAiHighRisk) {
        setIsShowingHighRiskModal(false)
        setHasAcknowledgedHighRisk(false)
      }
    }
  }, [isCustomPreferencesModalVisible, isReopeningAfterGenAiHighRisk])

  return {
    isGenAiHighRiskModalVisible: isShowingHighRiskModal,
    isReopeningAfterGenAiHighRisk,
    hasAcknowledgedHighRisk,
    onGenAiHighRiskDetected,
    onDismissGenAiHighRiskModal,
  }
}
